import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCheck, faMinus } from "@fortawesome/free-solid-svg-icons";
import { getStatusCardStyles } from "./SlotCard";

const ParkingDetailsPanel = ({ slots, onClose, onAllocate, onRelease }) => {
    if (!slots || slots.length === 0) return null;

    const isMulti = slots.length > 1;
    const primarySlot = slots[0];
    const cardStyles = getStatusCardStyles(primarySlot.status);

    return (
        <div className="w-[280px] bg-white dark:bg-dashboardbg border-l border-gray-200 dark:border-bordercolor shadow-lg lg:shadow-none flex flex-col absolute lg:static right-0 top-0 z-10 lg:z-auto h-full lg:h-auto transition-transform duration-300">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-bordercolor">
                <h2 className="text-sm font-bold text-gray-800 dark:text-textprimary">Parking Details</h2>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-textprimary transition-colors"
                >
                    <FontAwesomeIcon icon={faTimes} className="text-lg" />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto lg:overflow-y-visible p-3">
                <div className="flex items-center justify-between mb-3 gap-2">
                    <h3 className="text-base font-bold text-blue-600 dark:text-blue-400 truncate">
                        {isMulti ? `${slots.length} Slots Selected` : (primarySlot.slotNumber || primarySlot.groupName)}
                    </h3>
                    {!isMulti && (
                        <div className={`px-2 py-0.5 rounded text-xs font-bold capitalize border ${cardStyles}`}>
                            {primarySlot.status}
                        </div>
                    )}
                </div>

                <div className="space-y-3">
                    {/* Location */}
                    <div>
                        <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Location</h4>
                        {isMulti ? (
                            <div className="flex flex-col gap-1 text-[10px]">
                                <span className="text-gray-500 dark:text-textsecondary">Selected IDs:</span>
                                <span className="font-semibold text-gray-800 dark:text-textprimary break-words">
                                    {slots.map(s => s.slotNumber).join(", ")}
                                </span>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between text-[10px] mb-1.5">
                                    <span className="text-gray-500 dark:text-textsecondary">Floor</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.floorName || "—"}</span>
                                </div>
                                <div className="flex items-center justify-between text-[10px]">
                                    <span className="text-gray-500 dark:text-textsecondary">Allocation Type</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.allocationType || "General"}</span>
                                </div>
                            </>
                        )}
                    </div>

                    <hr className="border-gray-100 dark:border-bordercolor" />

                    {/* Assignment */}
                    {!isMulti && (
                        <>
                            <div>
                                <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Assignment</h4>
                                <div className="flex items-center justify-between text-[10px] mb-1.5">
                                    <span className="text-gray-500 dark:text-textsecondary">Assigned Unit</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.assignedUnit || "—"}</span>
                                </div>
                                <div className="flex items-center justify-between text-[10px] mb-1.5">
                                    <span className="text-gray-500 dark:text-textsecondary">Current Vehicle</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.currentVehicle || "—"}</span>
                                </div>
                                <div className="flex items-center justify-between text-[10px] mb-1.5">
                                    <span className="text-gray-500 dark:text-textsecondary">Current User</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.currentUser || "—"}</span>
                                </div>
                                <div className="flex items-center justify-between text-[10px] mb-1.5">
                                    <span className="text-gray-500 dark:text-textsecondary">Current Status</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary capitalize">{primarySlot.status}</span>
                                </div>
                                <div className="flex items-center justify-between text-[10px]">
                                    <span className="text-gray-500 dark:text-textsecondary">Last Updated</span>
                                    <span className="font-semibold text-gray-800 dark:text-textprimary">{primarySlot.lastUpdated || "—"}</span>
                                </div>
                            </div>

                            <hr className="border-gray-100 dark:border-bordercolor" />
                        </>
                    )}

                    {/* Quick Actions */}
                    <div>
                        <h4 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-2">Quick Actions</h4>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={onAllocate}
                                disabled={slots.some(s => s.status?.toLowerCase() === "occupied" || s.status?.toLowerCase() === "allocated")}
                                className={`flex flex-col items-center justify-center p-1.5 rounded-lg border transition-all duration-200 
                                    ${slots.some(s => s.status?.toLowerCase() === "occupied" || s.status?.toLowerCase() === "allocated")
                                        ? "opacity-50 cursor-not-allowed border-gray-200 dark:border-bordercolor bg-gray-50 dark:bg-actionitembg"
                                        : "border-gray-200 dark:border-bordercolor bg-white dark:bg-actionitembg hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400"
                                    }`}
                            >
                                <FontAwesomeIcon icon={faCheck} className="mb-0.5 text-xs" />
                                <span className="text-[9px] font-semibold">Allocate</span>
                            </button>

                            <button
                                onClick={onRelease}
                                disabled={slots.every(s => s.status === "Available")}
                                className={`flex flex-col items-center justify-center p-1.5 rounded-lg border transition-all duration-200 
                                    ${slots.every(s => s.status === "Available")
                                        ? "opacity-50 cursor-not-allowed border-gray-200 dark:border-bordercolor bg-gray-50 dark:bg-actionitembg"
                                        : "border-gray-200 dark:border-bordercolor bg-white dark:bg-actionitembg hover:border-red-500 hover:text-red-600 dark:hover:text-red-400"
                                    }`}
                            >
                                <FontAwesomeIcon icon={faTimes} className="mb-0.5 text-xs" />
                                <span className="text-[9px] font-semibold">Release</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParkingDetailsPanel;
